import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiLock, FiShield, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../../services/api';

const LinkAccess: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter a password');
      return;
    }

    if (attempts >= 5) {
      toast.error('Too many failed attempts. Please try again later.');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await api.verifyPassword({
        hash: hash!,
        password,
      });

      if (response.data.success) {
        toast.success('Password verified! Redirecting...');
        // Redirect to the original URL
        window.location.href = response.data.url;
      }
    } catch (error: any) {
      setAttempts(prev => prev + 1);
      
      if (error.response?.status === 401) {
        toast.error('Invalid password. Please try again.');
        setPassword('');
      } else if (error.response?.status === 404) {
        toast.error('Link not found');
        navigate('/');
      } else if (error.response?.status === 410) {
        toast.error(error.response.data.error || 'This link is no longer available');
        navigate('/');
      } else {
        toast.error('Failed to verify password. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <FiLock className="w-8 h-8 text-primary-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Password Protected Link
            </h1>
            <p className="text-gray-600">
              This link is protected. Please enter the password to continue.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="input"
                required
                autoFocus
                disabled={isVerifying}
              />
            </div>

            {attempts > 2 && (
              <div className="flex items-start space-x-2 p-3 bg-yellow-50 rounded-lg">
                <FiAlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-yellow-800">
                    {5 - attempts} attempts remaining
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isVerifying || attempts >= 5}
              className="btn btn-primary w-full"
            >
              {isVerifying ? 'Verifying...' : 'Access Link'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <FiShield className="w-4 h-4" />
              <span>Protected by AnonLink</span>
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              Your privacy is our priority. No tracking, no cookies.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkAccess;